import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimezoneInput } from "@/components/timezone-input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/auth/AuthProvider";
import { useCurrentUser } from "@/api/queries/me";
import {
  useDepartments,
  useProvisionChief,
  useUpdateDepartment,
  type DepartmentUpdate,
} from "@/api/queries/departments";
import { useInviteUser } from "@/api/queries/users";
import { useAppStore } from "@/store/app-store";
import { isRespaiAdmin } from "@/lib/permissions";

const inviteSchema = z.object({
  email: z.string().email(),
  given_name: z.string().min(1).max(80),
  family_name: z.string().min(1).max(80),
  roles: z
    .array(
      z.enum([
        "respai_admin",
        "respai_sales",
        "chief",
        "incident_commander",
        "firefighter",
      ]),
    )
    .min(1, "Select at least one role"),
});
type InviteForm = z.infer<typeof inviteSchema>;

type RoleOption = { value: InviteForm["roles"][number]; label: string };

// Fire-department roles, for a real department's roster.
const fireRoleOptions: RoleOption[] = [
  { value: "firefighter", label: "Firefighter" },
  { value: "incident_commander", label: "Incident commander" },
  { value: "chief", label: "Chief" },
];

// In the RespAI Internal tab, team members are platform staff, not firefighters,
// so only the platform roles are offered. The backend permits assigning either
// one only when the inviter is itself a respai_admin
// (user_invitation.PLATFORM_ONLY_ROLES).
//
// respai_sales exists so sandbox links can be generated without handing out a
// login that carries rls_bypass over every customer tenant. Until it was listed
// here there was no way to create one outside a hand-written INSERT — the API
// half of that fix is worthless without this line.
const internalRoleOptions: RoleOption[] = [
  { value: "respai_admin", label: "RespAI Admin" },
  { value: "respai_sales", label: "RespAI Sales — sandbox links only" },
];

const deptSchema = z.object({
  name: z.string().min(1),
  timezone: z.string().min(1),
  address: z.string().optional(),
  phone: z.string().optional(),
});
type DeptForm = z.infer<typeof deptSchema>;

const provisionSchema = z.object({
  name: z.string().min(1),
  jurisdiction: z.string().min(1),
  timezone: z.string().min(1),
  chief_email: z.string().email(),
  chief_given_name: z.string().min(1),
  chief_family_name: z.string().min(1),
});
type ProvisionForm = z.infer<typeof provisionSchema>;

export function SettingsPage() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const currentRole = me.data?.role ?? identity?.role;
  const isPlatformAdmin = isRespaiAdmin(currentRole);
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const { data: departments } = useDepartments();
  const selectedDept = departments?.find((d) => d.id === selectedDeptId) ?? null;
  const department = isPlatformAdmin ? selectedDept : (me.data?.department ?? null);
  const canProvisionDepartment = isPlatformAdmin;
  // The "RespAI Internal" tab is the platform admin's own home department. There the
  // people you invite are RespAI staff (respai_admin), not firefighters — so offer
  // the admin role and hide the fire roles. Everywhere else, invite fire roles.
  const isInternalDept =
    isPlatformAdmin &&
    !!selectedDeptId &&
    me.data?.home_department_id === selectedDeptId;
  const roleOptions = isInternalDept ? internalRoleOptions : fireRoleOptions;
  const invite = useInviteUser();
  const provisionChief = useProvisionChief();
  const updateDept = useUpdateDepartment(department?.id ?? "");
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [provisionResult, setProvisionResult] = useState<string | null>(null);

  const inviteForm = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      roles: ["firefighter"],
      email: "",
      given_name: "",
      family_name: "",
    },
  });

  // Default the role selection to the one that makes sense for the active tab once
  // it's known (internal → admin, otherwise firefighter), so the hidden fire roles
  // never linger as a stale selection in the internal tab.
  useEffect(() => {
    inviteForm.setValue("roles", isInternalDept ? ["respai_admin"] : ["firefighter"]);
  }, [isInternalDept, inviteForm]);

  const deptForm = useForm<DeptForm>({
    resolver: zodResolver(deptSchema),
    values: department
      ? {
          name: department.name,
          timezone: department.timezone,
          address: department.address ?? "",
          phone: department.phone ?? "",
        }
      : undefined,
  });

  const provisionForm = useForm<ProvisionForm>({
    resolver: zodResolver(provisionSchema),
    defaultValues: {
      name: "",
      jurisdiction: "",
      timezone: "America/St_Johns",
      chief_email: "",
      chief_given_name: "",
      chief_family_name: "",
    },
  });

  if (me.isLoading && !me.data && !canProvisionDepartment) {
    return <Skeleton className="h-64 w-full" />;
  }

  const meError =
    me.error instanceof Error
      ? me.error.message
      : "Could not load your admin profile.";
  const provisionError =
    provisionChief.error instanceof Error
      ? provisionChief.error.message
      : "Could not provision department.";
  const inviteError =
    invite.error instanceof Error
      ? invite.error.message
      : "Could not invite user.";
  const updateError =
    updateDept.error instanceof Error
      ? updateDept.error.message
      : "Could not update department.";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
      </header>

      {canProvisionDepartment ? (
        <Card>
          <CardHeader>
            <CardTitle>Provision department</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-3 sm:grid-cols-2"
              onSubmit={provisionForm.handleSubmit(async (values) => {
                const result = await provisionChief.mutateAsync(values);
                setProvisionResult(
                  `Created department ${result.department_id}`,
                );
                provisionForm.reset({
                  name: "",
                  jurisdiction: "",
                  timezone: "America/St_Johns",
                  chief_email: "",
                  chief_given_name: "",
                  chief_family_name: "",
                });
              })}
            >
              <Input
                placeholder="Department name"
                {...provisionForm.register("name")}
              />
              <Input
                placeholder="Jurisdiction"
                {...provisionForm.register("jurisdiction")}
              />
              <Controller
                control={provisionForm.control}
                name="timezone"
                render={({ field }) => (
                  <TimezoneInput
                    placeholder="Timezone"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Input
                placeholder="Chief email"
                {...provisionForm.register("chief_email")}
              />
              <Input
                placeholder="Chief given name"
                {...provisionForm.register("chief_given_name")}
              />
              <Input
                placeholder="Chief family name"
                {...provisionForm.register("chief_family_name")}
              />
              <Button
                className="sm:col-span-2"
                type="submit"
                disabled={provisionChief.isPending}
              >
                {provisionChief.isPending
                  ? "Provisioning…"
                  : "Create and invite chief"}
              </Button>
            </form>
            {provisionResult ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {provisionResult}
              </p>
            ) : null}
            {provisionChief.isError ? (
              <p className="mt-2 text-xs text-destructive">{provisionError}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {me.isError ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle>Admin profile needs attention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              You are signed in as {identity?.role ?? "an admin"}, but the API
              did not return a complete department profile yet.
            </p>
            <p className="text-destructive">{meError}</p>
            {canProvisionDepartment ? (
              <p>
                You can still use the provision form above to create the first
                department and invite its chief.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {!department ? (
        <Card>
          <CardHeader>
            <CardTitle>No department selected</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            {canProvisionDepartment ? (
              <p>
                Create the first department above. Once it exists, this page
                will show member invites and department metadata.
              </p>
            ) : (
              <p>
                Your account is not attached to a department yet. Ask a RespAI
                admin to provision or repair your department membership.
              </p>
            )}
          </CardContent>
        </Card>
      ) : null}

      {department ? (
        <Card>
          <CardHeader>
            <CardTitle>Invite user</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-3 sm:grid-cols-2"
              onSubmit={inviteForm.handleSubmit(async (values) => {
                const result = await invite.mutateAsync(values);
                setInviteResult(`Invited ${result.email}`);
                inviteForm.reset({
                  roles: ["firefighter"],
                  email: "",
                  given_name: "",
                  family_name: "",
                });
              })}
            >
              <Input
                placeholder="email@dept.ca"
                {...inviteForm.register("email")}
              />
              <Input
                placeholder="Given name"
                {...inviteForm.register("given_name")}
              />
              <Input
                placeholder="Family name"
                {...inviteForm.register("family_name")}
              />
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-input px-3 py-2 text-sm sm:col-span-2">
                {roleOptions.map((role) => (
                  <label key={role.value} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      value={role.value}
                      {...inviteForm.register("roles")}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
              <Button
                className="sm:col-span-2"
                type="submit"
                disabled={invite.isPending}
              >
                {invite.isPending ? "Inviting…" : "Invite"}
              </Button>
            </form>
            {inviteResult ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {inviteResult}
              </p>
            ) : null}
            {invite.isError ? (
              <p className="mt-2 text-xs text-destructive">{inviteError}</p>
            ) : null}
            {inviteForm.formState.errors.email ? (
              <p className="mt-2 text-xs text-destructive">
                {inviteForm.formState.errors.email.message}
              </p>
            ) : null}
            {inviteForm.formState.errors.roles ? (
              <p className="mt-2 text-xs text-destructive">
                {inviteForm.formState.errors.roles.message}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {department ? (
        <Card>
          <CardHeader>
            <CardTitle>Department metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-3 sm:grid-cols-2"
              onSubmit={deptForm.handleSubmit((values) =>
                updateDept.mutate(values as DepartmentUpdate),
              )}
            >
              <label className="text-sm">
                Name
                <Input className="mt-1" {...deptForm.register("name")} />
              </label>
              <div className="text-sm">
                Timezone
                <Controller
                  control={deptForm.control}
                  name="timezone"
                  render={({ field }) => (
                    <TimezoneInput
                      className="mt-1"
                      placeholder="America/St_Johns"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <label className="text-sm">
                Address
                <Input className="mt-1" {...deptForm.register("address")} />
              </label>
              <label className="text-sm">
                Phone
                <Input className="mt-1" {...deptForm.register("phone")} />
              </label>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={updateDept.isPending}>
                  {updateDept.isPending ? "Saving…" : "Save"}
                </Button>
                {updateDept.isError ? (
                  <p className="mt-2 text-xs text-destructive">{updateError}</p>
                ) : null}
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
